import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, ChevronRightIcon, LibraryIcon, ArrowRightIcon, FilterIcon, StarIcon, HeadphonesIcon, CheckCircleIcon, ClockIcon, SparklesIcon, MicIcon, FlameIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import AudiobookSeriesCarousel from '../components/Books/AudiobookSeriesCarousel';
import { audiobooks as realAudiobooks } from '../AudioBookDetails';
// Build audiobook series data from real audiobooks + mock expansion
const buildAudiobookSeries = () => {
  const seriesMap = new Map<string, any>();
  realAudiobooks.forEach(book => {
    if (!book.series?.name) return;
    const key = book.series.name;
    if (!seriesMap.has(key)) {
      seriesMap.set(key, {
        id: key.toLowerCase().replace(/\s+/g, '-'),
        name: key,
        author: book.author,
        narrators: [...new Set(book.narrators)],
        books: [],
        totalBooks: 0,
        genres: [book.genre, ...(book.subgenres || [])].slice(0, 2),
        rating: Math.round((4.5 + Math.random() * 0.4) * 10) / 10,
        reviews: Math.floor(300 + Math.random() * 1500),
        coverImage: book.coverImage,
        description: '',
        status: book.series.seriesComplete ? 'complete' : 'ongoing',
        popularity: Math.floor(70 + Math.random() * 30),
        totalAudioLength: book.audioLength,
        spiceLevel: book.spiceLevel,
        tropes: book.tropes?.slice(0, 3) || [],
        connectionType: 'connected'
      });
    }
    const series = seriesMap.get(key)!;
    series.books.push({
      id: `${key}-${book.series.number}`,
      title: book.title,
      coverImage: book.coverImage,
      position: book.series.number,
      audioLength: book.audioLength,
      narrator: book.narrators[0]
    });
    series.totalBooks = Math.max(series.totalBooks, series.books.length);
    book.narrators.forEach((n: string) => {
      if (!series.narrators.includes(n)) series.narrators.push(n);
    });
  });
  const descriptions: Record<string, string> = {
    'Madison Kate': 'A dark college romance series following Madison Kate as she navigates revenge, danger, and unexpected passion with three dangerous men.',
    'Zodiac Academy': 'Twin sisters discover they are Fae princesses at a magical academy where the heirs of the zodiac will stop at nothing to drive them out.',
    'Ice Planet Barbarians': 'When human women are stranded on an alien ice planet, they discover that the massive blue aliens might just be their fated mates.'
  };
  seriesMap.forEach((series, key) => {
    series.description = descriptions[key] || `An exciting audiobook series by ${series.author}.`;
  });
  const mockSeries = [{
    id: 'acotar',
    name: 'A Court of Thorns and Roses',
    author: 'Sarah J. Maas',
    narrators: ['Jennifer Ikeda'],
    books: [{
      id: 'acotar-1',
      title: 'A Court of Thorns and Roses',
      coverImage: 'https://images.unsplash.com/photo-1589998059171-988d887df646?w=400&h=600&fit=crop',
      position: 1,
      audioLength: '16h 7m',
      narrator: 'Jennifer Ikeda'
    }, {
      id: 'acotar-2',
      title: 'A Court of Mist and Fury',
      coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?w=400&h=600&fit=crop',
      position: 2,
      audioLength: '23h 16m',
      narrator: 'Jennifer Ikeda'
    }, {
      id: 'acotar-3',
      title: 'A Court of Wings and Ruin',
      coverImage: 'https://images.unsplash.com/photo-1603162525937-92a06da4b8ba?w=400&h=600&fit=crop',
      position: 3,
      audioLength: '25h 52m',
      narrator: 'Jennifer Ikeda'
    }],
    totalBooks: 5,
    genres: ['Fantasy', 'Romance'],
    rating: 4.7,
    reviews: 2890,
    coverImage: 'https://images.unsplash.com/photo-1589998059171-988d887df646?w=400&h=600&fit=crop',
    description: 'A mortal huntress is dragged into the magical land of the Fae, where she discovers that her captor is not what he seems.',
    status: 'ongoing',
    popularity: 98,
    totalAudioLength: '65h+',
    spiceLevel: 4,
    tropes: ['Enemies to Lovers', 'Fae Romance', 'Chosen One'],
    connectionType: 'connected'
  }, {
    id: 'fourth-wing',
    name: 'The Empyrean',
    author: 'Rebecca Yarros',
    narrators: ['Rebecca Soler', 'Teddy Hamilton'],
    books: [{
      id: 'fw-1',
      title: 'Fourth Wing',
      coverImage: 'https://images.unsplash.com/photo-1531928351158-2f736078e0a1?w=400&h=600&fit=crop',
      position: 1,
      audioLength: '20h 47m',
      narrator: 'Rebecca Soler'
    }, {
      id: 'fw-2',
      title: 'Iron Flame',
      coverImage: 'https://images.unsplash.com/photo-1614332287897-cdc485fa562d?w=400&h=600&fit=crop',
      position: 2,
      audioLength: '25h 30m',
      narrator: 'Rebecca Soler'
    }],
    totalBooks: 5,
    genres: ['Fantasy', 'Romance'],
    rating: 4.6,
    reviews: 3450,
    coverImage: 'https://images.unsplash.com/photo-1531928351158-2f736078e0a1?w=400&h=600&fit=crop',
    description: 'At a war college for dragon riders, a young woman with a fragile body must survive the most brutal training or die trying.',
    status: 'ongoing',
    popularity: 97,
    totalAudioLength: '46h+',
    spiceLevel: 4,
    tropes: ['Dragon Riders', 'Enemies to Lovers', 'Academy'],
    connectionType: 'connected'
  }, {
    id: 'haunting-adeline',
    name: 'Cat and Mouse',
    author: 'H.D. Carlton',
    narrators: ['Teddy Hamilton', 'Michelle Sparks'],
    books: [{
      id: 'ha-1',
      title: 'Haunting Adeline',
      coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?w=400&h=600&fit=crop',
      position: 1,
      audioLength: '15h 44m',
      narrator: 'Teddy Hamilton'
    }, {
      id: 'ha-2',
      title: 'Hunting Adeline',
      coverImage: 'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?w=400&h=600&fit=crop',
      position: 2,
      audioLength: '18h 2m',
      narrator: 'Teddy Hamilton'
    }],
    totalBooks: 2,
    genres: ['Romance', 'Dark Romance'],
    rating: 4.3,
    reviews: 1876,
    coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?w=400&h=600&fit=crop',
    description: 'A dark and twisted tale of obsession where a shadow watches from the dark, and the manipulator becomes the manipulated.',
    status: 'complete',
    popularity: 92,
    totalAudioLength: '33h 46m',
    spiceLevel: 5,
    tropes: ['Stalker Romance', 'Dark Hero', 'Obsession'],
    connectionType: 'connected'
  }, {
    id: 'twisted',
    name: 'Twisted',
    author: 'Ana Huang',
    narrators: ['Cindy Kay', 'Shane East'],
    books: [{
      id: 'tw-1',
      title: 'Twisted Love',
      coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=600&fit=crop',
      position: 1,
      audioLength: '9h 30m',
      narrator: 'Cindy Kay'
    }, {
      id: 'tw-2',
      title: 'Twisted Games',
      coverImage: 'https://images.unsplash.com/photo-1580934738404-5eb8d4c0389e?w=400&h=600&fit=crop',
      position: 2,
      audioLength: '10h 15m',
      narrator: 'Cindy Kay'
    }, {
      id: 'tw-3',
      title: 'Twisted Hate',
      coverImage: 'https://images.unsplash.com/photo-1629992101753-56d196c8aabb?w=400&h=600&fit=crop',
      position: 3,
      audioLength: '12h 45m',
      narrator: 'Shane East'
    }, {
      id: 'tw-4',
      title: 'Twisted Lies',
      coverImage: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=400&h=600&fit=crop',
      position: 4,
      audioLength: '11h 20m',
      narrator: 'Shane East'
    }],
    totalBooks: 4,
    genres: ['Romance', 'Contemporary'],
    rating: 4.5,
    reviews: 2100,
    coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=600&fit=crop',
    description: 'Four interconnected stories of love, obsession, and the thin line between desire and destruction.',
    status: 'complete',
    popularity: 90,
    totalAudioLength: '43h 50m',
    spiceLevel: 4,
    tropes: ['Billionaire', 'Enemies to Lovers', 'Forbidden Love'],
    connectionType: 'interconnected'
  }, {
    id: 'throne-of-glass',
    name: 'Throne of Glass',
    author: 'Sarah J. Maas',
    narrators: ['Elizabeth Evans'],
    books: [{
      id: 'tog-1',
      title: 'Throne of Glass',
      coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?w=400&h=600&fit=crop',
      position: 1,
      audioLength: '13h 3m',
      narrator: 'Elizabeth Evans'
    }, {
      id: 'tog-2',
      title: 'Crown of Midnight',
      coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=400&h=600&fit=crop',
      position: 2,
      audioLength: '12h 47m',
      narrator: 'Elizabeth Evans'
    }],
    totalBooks: 8,
    genres: ['Fantasy', 'Adventure'],
    rating: 4.6,
    reviews: 1950,
    coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?w=400&h=600&fit=crop',
    description: 'An assassin must fight for her freedom in a corrupt kingdom, uncovering a destiny far greater than she ever imagined.',
    status: 'complete',
    popularity: 94,
    totalAudioLength: '100h+',
    spiceLevel: 3,
    tropes: ['Assassin Heroine', 'Chosen One', 'Love Triangle'],
    connectionType: 'connected'
  }];
  return [...Array.from(seriesMap.values()), ...mockSeries];
};
const AudiobookSeries = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('popularity');
  const allSeries = useMemo(() => buildAudiobookSeries(), []);
  const activeFilterCount = selectedStatuses.length + selectedGenres.length + (sortBy !== 'popularity' ? 1 : 0);
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'popularity',
      label: 'Most Popular'
    }, {
      value: 'rating',
      label: 'Highest Rated'
    }, {
      value: 'books',
      label: 'Most Books'
    }, {
      value: 'alpha',
      label: 'A–Z'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'status',
    title: 'Series Status',
    type: 'checkbox',
    options: [{
      value: 'complete',
      label: 'Complete'
    }, {
      value: 'ongoing',
      label: 'Ongoing'
    }],
    value: selectedStatuses,
    onChange: (val: string) => setSelectedStatuses(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: [{
      value: 'Fantasy',
      label: 'Fantasy'
    }, {
      value: 'Romance',
      label: 'Romance'
    }, {
      value: 'Dark Romance',
      label: 'Dark Romance'
    }, {
      value: 'Science Fiction',
      label: 'Sci-Fi'
    }, {
      value: 'Contemporary',
      label: 'Contemporary'
    }, {
      value: 'Adventure',
      label: 'Adventure'
    }],
    value: selectedGenres,
    onChange: (val: string) => setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  const resetFilters = () => {
    setSelectedStatuses([]);
    setSelectedGenres([]);
    setSortBy('popularity');
    setSearchTerm('');
  };
  const applyFilters = (data: any[]) => {
    let result = [...data];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q) || s.author.toLowerCase().includes(q) || s.narrators?.some((n: string) => n.toLowerCase().includes(q)));
    }
    if (selectedStatuses.length > 0) {
      result = result.filter(s => selectedStatuses.includes(s.status));
    }
    if (selectedGenres.length > 0) {
      result = result.filter(s => s.genres.some((g: string) => selectedGenres.includes(g)));
    }
    if (sortBy === 'rating') result.sort((a, b) => b.rating - a.rating);else if (sortBy === 'alpha') result.sort((a, b) => a.name.localeCompare(b.name));else if (sortBy === 'books') result.sort((a, b) => b.totalBooks - a.totalBooks);else result.sort((a, b) => b.popularity - a.popularity);
    return result;
  };
  const completedSeries = applyFilters(allSeries.filter(s => s.status === 'complete'));
  const ongoingSeries = applyFilters(allSeries.filter(s => s.status === 'ongoing'));
  const fantasySeries = applyFilters(allSeries.filter(s => s.genres.includes('Fantasy')));
  const romanceSeries = applyFilters(allSeries.filter(s => s.genres.includes('Romance') || s.genres.includes('Dark Romance')));
  const featuredSeries = allSeries.find(s => s.id === 'fourth-wing') || allSeries[0];
  const getGenreColor = (genre: string): string => {
    const g = genre.toLowerCase();
    if (g.includes('romance')) return 'bg-pink-600';
    if (g.includes('fantasy')) return 'bg-indigo-600';
    if (g.includes('sci-fi') || g.includes('science')) return 'bg-blue-600';
    if (g.includes('contemporary')) return 'bg-emerald-600';
    if (g.includes('adventure')) return 'bg-amber-600';
    return 'bg-violet-600';
  };
  const hasAnyResults = completedSeries.length > 0 || ongoingSeries.length > 0 || fantasySeries.length > 0 || romanceSeries.length > 0;
  return <div className={`min-h-screen ${dk.page}`} data-id="element-6027" data-mp="3nutwsm9udanl">
      {/* Header */}
      <div className={`py-6 px-4 ${dk.surface}`} data-id="element-6028" data-mp="6dkeup6ob7eg">
        <div className="max-w-7xl mx-auto" data-id="element-6029" data-mp="6c9588qjr5ff">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-6030" data-mp="1eh9t4qp116h">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-6031" data-mp="1gk9kghne8oe">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-6032" data-mp="34w98t338yle6" />
            <Link to="/audiobooks" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-6033" data-mp="3jsgu66qe5sv1">
              Audiobooks
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-6034" data-mp="3evgldo25qc08" />
            <span className={dk.textPrimary} data-id="element-6035" data-mp="z16k01vjuk3f">Series</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-6036" data-mp="u3erb5n2yty6">
            <div className="flex items-center gap-3" data-id="element-6037" data-mp="116lb9ytw1i8n">
              <div className="relative" data-id="element-6038" data-mp="115tievrodym2">
                <LibraryIcon className={`h-8 w-8 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-6039" data-mp="16561d5ffeljd" />
                <HeadphonesIcon className={`h-4 w-4 absolute -bottom-1 -right-1 ${dk.textSecondary}`} data-id="element-6040" data-mp="1b5u3am570msd" />
              </div>
              <div data-id="element-6041" data-mp="ig3m2489l4s1">
                <h1 className={`text-2xl md:text-3xl font-bold ${dk.textHeading}`} data-id="element-6042" data-mp="igmtgr0agart">
                  Audiobook Series
                </h1>
                <p className={`mt-1 text-sm ${dk.textSecondary}`} data-id="element-6043" data-mp="se160fj3cfeu">
                  Binge-worthy series available in audio — from complete sagas
                  to ongoing adventures.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-6044" data-mp="3vtyk2zkdi7ig">
              <div className="relative flex-grow md:w-80" data-id="element-6045" data-mp="4nx3kbhn2lpf">
                <input type="text" placeholder="Search series, authors, or narrators..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full px-5 py-2.5 pl-10 rounded-lg border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.input}`} data-id="element-6046" data-mp="4mc9so35f6sm" />
                <SearchIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-6047" data-mp="23yp87x9fj7uc" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-6048" data-mp="1q6r5c46ba86m">
        {/* Mobile Filter Button */}
        <div className="mb-6 lg:hidden" data-id="element-6049" data-mp="1l98vmbkgzoxk">
          <button onClick={() => setMobileFiltersOpen(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border shadow-sm ${dk.card} ${dk.textSecondary}`} data-id="element-6050" data-mp="1l6cvcgl5go5y">
            <FilterIcon className="h-4 w-4" data-id="element-6051" data-mp="1bccr0gxozmya" />
            Filters
            {activeFilterCount > 0 && <span className={`ml-1 px-1.5 py-0.5 ${rt.badgeRadius} text-xs font-bold text-white ${rt.bgDark}`} data-id="element-6052" data-mp="302mm1ugs3ewq">
                {activeFilterCount}
              </span>}
          </button>
        </div>

        <div className="flex gap-8" data-id="element-6053" data-mp="2v4bbfhint0dg">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-6054" data-mp="2ggq8bnlsb15j">
            <FilterSidebar sections={filterSections} onReset={resetFilters} data-id="element-6055" data-mp="27hmoghesmhpz" />
          </FilterSidebarWrapper>

          {/* Discovery Sections */}
          <div className="flex-1 min-w-0 space-y-12" data-id="element-6056" data-mp="1xhvpa2fafejk">
            {/* Featured Series Hero */}
            {featuredSeries && <section className={`rounded-xl overflow-hidden border ${dk.border} ${dk.card}`} data-id="element-6057" data-mp="22fdsdc6mizjn">
                <div className="flex flex-col md:flex-row md:max-h-52" data-id="element-6058" data-mp="1iwzrd1gskveb">
                  <div className="w-full md:w-1/3 relative" data-id="element-6059" data-mp="1eumxbabeqlxd">
                    <img src={featuredSeries.coverImage} alt={featuredSeries.name} className="w-full h-52 md:h-full object-cover" data-id="element-6060" data-mp="1sstfli3vidbx" />
                    {featuredSeries.genres[0] && <span className={`absolute top-3 left-3 text-white text-xs font-medium px-2 py-0.5 rounded-full ${getGenreColor(featuredSeries.genres[0])}`} data-id="element-6061" data-mp="21rolsu6x3nf8">
                        {featuredSeries.genres[0]}
                      </span>}
                    <div className="absolute top-3 right-3" data-id="element-6062" data-mp="2lhrxce433dgp">
                      <span className={`text-white text-xs px-2 py-0.5 rounded-full ${featuredSeries.status === 'complete' ? 'bg-green-600' : 'bg-yellow-500'}`} data-id="element-6063" data-mp="2li9ztshcanx2">
                        {featuredSeries.status === 'complete' ? 'Complete' : 'Ongoing'}
                      </span>
                    </div>
                  </div>
                  <div className={`w-full md:w-2/3 p-5 flex flex-col justify-between ${dk.isDark ? 'bg-gray-800' : 'bg-gray-50'}`} data-id="element-6064" data-mp="31zr4ipow8eca">
                    <div data-id="element-6065" data-mp="2abfhffpuwz6e">
                      <div className="flex items-center gap-2 mb-1" data-id="element-6066" data-mp="2a8jknupqwgjj">
                        <SparklesIcon className={`h-4 w-4 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-6067" data-mp="25de2n6zgrm0q" />
                        <span className={`text-xs font-semibold uppercase tracking-wide ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-6068" data-mp="2ec97aemv0o1x">
                          Featured Series
                        </span>
                        <span className={`text-xs ${dk.textMuted}`} data-id="element-6069" data-mp="1mjigijny3rk2">
                          · {featuredSeries.totalBooks} books ·{' '}
                          {featuredSeries.totalAudioLength}
                        </span>
                      </div>
                      <h2 className={`text-lg font-bold mb-0.5 ${dk.textHeading}`} data-id="element-6070" data-mp="12kjkvmrryog3">
                        {featuredSeries.name}
                      </h2>
                      <p className={`text-xs mb-1 ${dk.textSecondary}`} data-id="element-6071" data-mp="1v8fhkrhcggcv">
                        by {featuredSeries.author}
                      </p>

                      {/* Narrators */}
                      <div className={`flex items-center gap-1 text-xs mb-2 ${dk.textMuted}`} data-id="element-6072" data-mp="1l8wy0c5ciwlc">
                        <MicIcon className="h-3 w-3 flex-shrink-0" data-id="element-6073" data-mp="1qamdq53ijyfr" />
                        <span className="line-clamp-1" data-id="element-6074" data-mp="9t00w0nct6d1">
                          {featuredSeries.narrators?.join(', ')}
                        </span>
                      </div>

                      <p className={`text-xs mb-2 line-clamp-2 ${dk.textSecondary}`} data-id="element-6075" data-mp="2hf9w0k6acoch">
                        {featuredSeries.description}
                      </p>

                      {/* Tropes */}
                      <div className="flex flex-wrap gap-1" data-id="element-6076" data-mp="27o5htmofp7ip">
                        {featuredSeries.tropes?.slice(0, 3).map((t: string) => <span key={t} className={`text-[10px] px-2 py-0.5 rounded-full ${dk.isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`} data-id="element-6077" data-mp="1xtdf64fzvuh2">
                            {t}
                          </span>)}
                        {featuredSeries.spiceLevel > 0 && <span className={`inline-flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full ${dk.isDark ? 'bg-rose-900/40 text-rose-300' : 'bg-rose-50 text-rose-700'}`} data-id="element-6078" data-mp="3j4c9lz2f1t31">
                            <FlameIcon className="h-2.5 w-2.5" data-id="element-6079" data-mp="3qp6ata5uqwm9" />
                            {'🔥'.repeat(featuredSeries.spiceLevel)}
                          </span>}
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 mt-3" data-id="element-6080" data-mp="48a6ad0yu0rn">
                      <div className="flex items-center gap-1" data-id="element-6081" data-mp="32s0ojx3kfxr1">
                        <StarIcon className="h-4 w-4 text-amber-400 fill-amber-400" data-id="element-6082" data-mp="37qbwu3e7q075" />
                        <span className={`text-sm font-semibold ${dk.textPrimary}`} data-id="element-6083" data-mp="37semgmbgdrbc">
                          {featuredSeries.rating}
                        </span>
                        <span className={`text-xs hidden md:inline ${dk.textMuted}`} data-id="element-6084" data-mp="rz65hv7r3y8x">
                          ({featuredSeries.reviews.toLocaleString()} reviews)
                        </span>
                      </div>
                      <Link to={`/series/${featuredSeries.id}`} className={`flex items-center justify-center px-4 py-1.5 rounded-lg font-medium text-sm text-white transition-colors ${dk.isDark ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-indigo-600 hover:bg-indigo-700'}`} data-id="element-6085" data-mp="ulonw62wzgxs">
                        View Series <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-6086" data-mp="1dhomw0ub7kqv" />
                      </Link>
                    </div>
                  </div>
                </div>
              </section>}

            {/* Complete & Binge-Ready */}
            {completedSeries.length > 0 && <section data-id="element-6087" data-mp="1i8wssmwfknza">
                <div className="flex items-center justify-between mb-4" data-id="element-6088" data-mp="1iahmybeu24wm">
                  <div className="flex items-center gap-2" data-id="element-6089" data-mp="1x5nw37986ju6">
                    <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-6090" data-mp="1x42yt9vj0msm" />
                    <div data-id="element-6091" data-mp="1s8xdod9y7c5l">
                      <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-6092" data-mp="1s5jd9akuhd0s">
                        Complete & Binge-Ready
                      </h2>
                      <p className={`text-sm ${dk.textSecondary}`} data-id="element-6093" data-mp="2ftpwd4rpqa0m">
                        Full series available — no waiting required.
                      </p>
                    </div>
                  </div>
                  <Link to="/audiobooks/series?status=complete" className={`text-sm font-medium flex items-center ${dk.isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`} data-id="element-6094" data-mp="26si1kwoenrba">
                    See all <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6095" data-mp="1j5jn24juitg0" />
                  </Link>
                </div>
                <AudiobookSeriesCarousel series={completedSeries} darkMode={dk.isDark} data-id="element-6096" data-mp="1o4d5mbdzpan1" />
              </section>}

            {/* Ongoing Sagas */}
            {ongoingSeries.length > 0 && <section data-id="element-6097" data-mp="v0irinvqzwmx">
                <div className="flex items-center justify-between mb-4" data-id="element-6098" data-mp="v10z2gnv1xa5">
                  <div className="flex items-center gap-2" data-id="element-6099" data-mp="qac3ap55ee57">
                    <ClockIcon className="h-6 w-6 text-blue-500" data-id="element-6100" data-mp="q8rap52e959z" />
                    <div data-id="element-6101" data-mp="lc0tgeeus3mz">
                      <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-6102" data-mp="lb8z16wzsbya">
                        Ongoing Sagas
                      </h2>
                      <p className={`text-sm ${dk.textSecondary}`} data-id="element-6103" data-mp="1dzd4psfkcp0z">
                        Follow along as new audiobooks release.
                      </p>
                    </div>
                  </div>
                  <Link to="/audiobooks/series?status=ongoing" className={`text-sm font-medium flex items-center ${dk.isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`} data-id="element-6104" data-mp="140xpampwf47b">
                    See all <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6105" data-mp="3jslajkvjv8aa" />
                  </Link>
                </div>
                <AudiobookSeriesCarousel series={ongoingSeries} darkMode={dk.isDark} data-id="element-6106" data-mp="1zauavdfcseoq" />
              </section>}

            {/* Epic Fantasy */}
            {fantasySeries.length > 0 && <section data-id="element-6107" data-mp="297ffhxngnns1">
                <div className="flex items-center justify-between mb-4" data-id="element-6108" data-mp="296x9i9azxv6x">
                  <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-6109" data-mp="2e0rr9771k03d">
                    Epic Fantasy Series
                  </h2>
                  <Link to="/audiobooks/fantasy" className={`text-sm font-medium flex items-center ${dk.isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`} data-id="element-6110" data-mp="2n055ed641cuf">
                    See all <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6111" data-mp="2wxjgdx99l9fc" />
                  </Link>
                </div>
                <AudiobookSeriesCarousel series={fantasySeries} darkMode={dk.isDark} data-id="element-6112" data-mp="xnbfhuofvv2l" />
              </section>}

            {/* Romance */}
            {romanceSeries.length > 0 && <section data-id="element-6113" data-mp="2zjitbqvyrw72">
                <div className="flex items-center justify-between mb-4" data-id="element-6114" data-mp="2zi7qpue98uhj">
                  <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-6115" data-mp="3e7wxq6913spq">
                    Romance Series
                  </h2>
                  <Link to="/audiobooks/romance" className={`text-sm font-medium flex items-center ${dk.isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`} data-id="element-6116" data-mp="399c5ou3xkfcd">
                    See all <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-6117" data-mp="2vhfdyr66c7gi" />
                  </Link>
                </div>
                <AudiobookSeriesCarousel series={romanceSeries} darkMode={dk.isDark} data-id="element-6118" data-mp="2qh1almfiplot" />
              </section>}

            {/* Empty state */}
            {!hasAnyResults && <div className="text-center py-16" data-id="element-6119" data-mp="5u6coqymd2ks">
                <HeadphonesIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-6120" data-mp="dabah1rbz395" />
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-6121" data-mp="ic0nuo20zt0e">
                  No series found
                </h3>
                <p className={`mb-4 ${dk.textSecondary}`} data-id="element-6122" data-mp="8c9syl9jzbzs">
                  Try adjusting your filters or search term.
                </p>
                <button onClick={resetFilters} className={`font-medium ${rt.accentLink}`} data-id="element-6123" data-mp="w2vojd2df3sw">
                  Clear filters
                </button>
              </div>}

            {/* Bottom CTA */}
            <div className={`rounded-xl p-8 text-center border ${dk.border} ${dk.isDark ? 'bg-indigo-900/20' : 'bg-indigo-50'}`} data-id="element-6124" data-mp="rb45ropgi2db">
              <HeadphonesIcon className={`h-12 w-12 mx-auto mb-4 ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-6125" data-mp="1ez5eoyqjrkt9" />
              <h2 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-6126" data-mp="2l8ym4rwy70ri">
                Explore All Audiobook Series
              </h2>
              <p className={`max-w-lg mx-auto mb-6 ${dk.textSecondary}`} data-id="element-6127" data-mp="2p8h6nmkux9tt">
                Browse hundreds of series with filters for genre, spice level,
                narrator, and completion status.
              </p>
              <Link to="/audiobooks" className={`inline-flex items-center px-6 py-3 rounded-lg font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 ${rt.bgDark} hover:opacity-90`} data-id="element-6128" data-mp="2z1y02ph30mwy">
                Browse All Audiobooks
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default AudiobookSeries;